/**
 * Utility functions for managing modal display timing with localStorage
 */

const MODAL_DISPLAY_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Check if enough time has passed since the last modal display to show it again
 */
export function shouldShowModalBasedOnTime(actionKey: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const lastShownTimestamp = localStorage.getItem(actionKey);

    if (!lastShownTimestamp) {
      // Modal has never been shown, so it should be shown
      return true;
    }

    const lastShown = parseInt(lastShownTimestamp, 10);
    const now = Date.now();

    // Check if 30 minutes have passed since last display
    return now - lastShown >= MODAL_DISPLAY_INTERVAL;
  } catch (error) {
    console.warn(`Failed to check modal timing for ${actionKey}:`, error);
    return false;
  }
}

/**
 * Mark that a modal was displayed at the current time
 */
export function markModalDisplayed(actionKey: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const now = Date.now();
    localStorage.setItem(actionKey, now.toString());
  } catch (error) {
    console.warn(`Failed to mark modal displayed for ${actionKey}:`, error);
  }
}

/**
 * Get the last time a modal was displayed (for debugging/testing)
 */
export function getLastModalDisplayTime(actionKey: string): number | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const lastShownTimestamp = localStorage.getItem(actionKey);
    return lastShownTimestamp ? parseInt(lastShownTimestamp, 10) : null;
  } catch (error) {
    console.warn(
      `Failed to get last modal display time for ${actionKey}:`,
      error,
    );
    return null;
  }
}

/**
 * Clear modal display history for a specific action (useful for testing/debugging)
 */
export function clearModalDisplayHistory(actionKey: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(actionKey);
  } catch (error) {
    console.warn(
      `Failed to clear modal display history for ${actionKey}:`,
      error,
    );
  }
}

/**
 * Get time remaining until modal can be shown again (in milliseconds)
 */
export function getTimeUntilNextModalDisplay(actionKey: string): number {
  if (typeof window === "undefined") {
    return 0;
  }

  try {
    const lastShownTimestamp = localStorage.getItem(actionKey);

    if (!lastShownTimestamp) {
      return 0; // Can be shown immediately
    }

    const lastShown = parseInt(lastShownTimestamp, 10);
    const now = Date.now();
    const elapsed = now - lastShown;

    return Math.max(0, MODAL_DISPLAY_INTERVAL - elapsed);
  } catch (error) {
    console.warn(
      `Failed to calculate time until next modal display for ${actionKey}:`,
      error,
    );
    return 0;
  }
}
